/**
 * Whether the phone has been taking our renderer away, and whether it crashed
 * or was simply reclaimed.
 *
 * The chart is a live WebGL map, and a phone that wants memory back will take
 * the WebView's renderer process from a backgrounded app without asking. That
 * is a plausible reading of an anchor watch that came back in the morning
 * showing nothing but black, and it is not a question the web app can answer
 * about itself: by the time JavaScript runs again, the process that died is
 * gone and so is any trace of it.
 *
 * So the Android side writes each one down (see MainActivity.recordRendererDeath)
 * and we read the file on the way back up. The line to look for is didCrash:
 *
 *   false - Android took the renderer to free memory. We were expendable, and
 *           the answer is to need less memory, or to survive being reclaimed.
 *   true  - it fell over on its own, which is a different hunt entirely.
 *
 * Read once at startup and cached, because the bug-report context is built
 * synchronously and a file read is not. Nothing here throws: on the web, on a
 * phone with no such file, or on any error at all, it reports nothing rather
 * than getting in the way.
 */
import { Capacitor } from '@capacitor/core';

const DEATH_LOG = 'renderer-deaths.log';

let deaths: string[] = [];

/** The recorded renderer deaths, oldest first. Empty until loaded, or if none. */
export function rendererDeaths(): string[] {
  return deaths;
}

/**
 * One line for a bug report, or null when there is nothing to say. Kept
 * separate from the raw lines so a report can lead with the summary.
 */
export function rendererDeathSummary(): string | null {
  if (!deaths.length) return null;
  const crashes = deaths.filter((l) => l.includes('didCrash=true')).length;
  const reclaimed = deaths.length - crashes;
  const last = deaths[deaths.length - 1];
  return `${deaths.length} renderer death(s): ${reclaimed} reclaimed by Android, ${crashes} crashed. Last: ${last}`;
}

/** Parse the file's contents into lines. Pure, so the parsing is testable. */
export function parseDeathLog(raw: string): string[] {
  return raw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

/**
 * Read the log once, on the way up. Safe to call anywhere; safe to call twice.
 * Never rejects.
 */
export async function loadRendererDeaths(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
    const res = await Filesystem.readFile({
      path: DEATH_LOG,
      // Directory.Data is the app's own files dir, which is where the activity
      // writes it. Same folder, both sides.
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    deaths = parseDeathLog(typeof res.data === 'string' ? res.data : '');
  } catch {
    // No file yet is the normal, happy case: the renderer has never died.
  }
}
